// Cloud sync for ToneGuard — mirrors every `toneguard:*` localStorage
// key to MongoDB via /api/user-state, so a user's chats, insights, session
// log, ToneMirror Layer 4 state, Layer 1 history, and prefs persist across
// devices.
//
// Architecture:
//   • Storage stays in localStorage (instant reads, no UI changes).
//   • On login → bootstrap: GET /api/user-state, reconcile with local,
//     write the merged snapshot back to localStorage, then push it to
//     the server so both sides agree.
//   • On every subsequent write to a `toneguard:*` key, a debounced PUT
//     uploads the full snapshot. The storage-scope wrapper calls our
//     `notifyWrite()` hook on each setItem/removeItem.
//   • Logout / user switch tears down the timer.
//
// Reconciliation policy:
//   1. If server has a non-empty `toneguard:v2` chat store AND its
//      `client_updated_at` is newer than the local copy → server wins.
//   2. Otherwise → local wins (first-time push, or this device has newer
//      edits). Local data is preserved AND pushed to the server.
//
// All values are stored as raw JSON strings (mirroring localStorage),
// keeping the wire format identical to the on-disk format. The frontend
// schema can evolve without backend changes.

import axios from "axios";
import { __originalStorage } from "@/lib/storageScope";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATE_URL = `${API}/user-state`;
const PREFIX = "toneguard:";
const PUSH_DEBOUNCE_MS = 1500;
// Keep the bootstrap timeout generous — networks can be slow on first
// load and we don't want to lose the user's data. If the GET fails we
// fall through to local-only mode and try again on next write.
const BOOTSTRAP_TIMEOUT_MS = 12_000;
const PUSH_TIMEOUT_MS = 12_000;

// Top-level localStorage key that stores the most recent `client_updated_at`
// stamp for this device. Used for last-write-wins reconciliation.
const META_KEY = "toneguard:_meta";

let _userId = null;
let _state = "idle"; // 'idle' | 'bootstrapping' | 'live'
let _pushTimer = null;
let _pushInFlight = null;

// ─── Local snapshot helpers ─────────────────────────────────────────────

/**
 * Collect every `toneguard:*` key from localStorage into a flat object
 * the server endpoint understands. Reads via the ORIGINAL (unscoped)
 * localStorage so we always grab the keys for the active user only —
 * storageScope has already namespaced them under `:u:<user_id>`.
 *
 * Returns the UNSCOPED keys (with `toneguard:` prefix) so the server
 * sees the same names every device uses.
 */
function readLocalSnapshot() {
    const data = {};
    if (typeof window === "undefined" || !window.localStorage) {
        return { data, client_updated_at: null };
    }
    const orig = __originalStorage();
    const suffix = _userId ? `:u:${_userId}` : null;
    const total = orig.length;
    for (let i = 0; i < total; i += 1) {
        const rawKey = orig.key(i);
        if (typeof rawKey !== "string") continue;
        // We only collect keys that belong to the active user. Two cases:
        //   1. Scoped key like `toneguard:v2:u:<userId>` → strip suffix.
        //   2. Legacy / unscoped key like `toneguard:v2` (anonymous mode
        //      before storageScope was active) → take as-is. This catches
        //      first-time bootstraps on devices that had pre-auth data.
        let key = null;
        if (suffix && rawKey.endsWith(suffix)) {
            key = rawKey.slice(0, -suffix.length);
        } else if (rawKey.startsWith(PREFIX) && !rawKey.includes(":u:")) {
            key = rawKey;
        }
        if (!key || !key.startsWith(PREFIX)) continue;
        // Skip the meta key — we re-derive it below so the snapshot
        // always carries the current client_updated_at.
        if (key === META_KEY) continue;
        const value = orig.getItem(rawKey);
        if (typeof value === "string") data[key] = value;
    }
    // Read the meta separately so we can return it on the wrapper object.
    const meta = orig.getItem(suffix ? `${META_KEY}${suffix}` : META_KEY);
    let client_updated_at = null;
    if (meta) {
        try {
            const parsed = JSON.parse(meta);
            client_updated_at =
                typeof parsed?.client_updated_at === "string"
                    ? parsed.client_updated_at
                    : null;
        } catch {
            /* corrupt — ignore */
        }
    }
    return { data, client_updated_at };
}

/**
 * Write a server snapshot into localStorage for the active user. All keys
 * are re-namespaced through the scoped wrapper so the rest of the app
 * reads them via the standard scoped paths.
 */
function applyServerSnapshot(serverData, serverClientUpdatedAt) {
    if (!serverData || typeof serverData !== "object") return;
    const orig = __originalStorage();
    const suffix = _userId ? `:u:${_userId}` : null;
    // 1. Wipe every existing scoped `toneguard:*` key so we don't leave
    //    stale data behind (e.g., chats that exist locally but were
    //    deleted on another device).
    const toRemove = [];
    const total = orig.length;
    for (let i = 0; i < total; i += 1) {
        const rawKey = orig.key(i);
        if (typeof rawKey !== "string") continue;
        if (suffix && rawKey.endsWith(suffix) && rawKey.startsWith(PREFIX)) {
            toRemove.push(rawKey);
        }
    }
    for (const k of toRemove) orig.removeItem(k);
    // 2. Write each key from the server payload under its scoped name.
    for (const [k, v] of Object.entries(serverData)) {
        if (typeof k !== "string" || !k.startsWith(PREFIX)) continue;
        if (typeof v !== "string") continue;
        const scoped = suffix ? `${k}${suffix}` : k;
        orig.setItem(scoped, v);
    }
    // 3. Stamp meta with the server's notion of client_updated_at so a
    //    subsequent local edit can compare against it next time.
    const stamp = serverClientUpdatedAt || new Date().toISOString();
    const metaScoped = suffix ? `${META_KEY}${suffix}` : META_KEY;
    orig.setItem(metaScoped, JSON.stringify({ client_updated_at: stamp }));
}

function stampMetaNow() {
    if (!_userId) return null;
    const orig = __originalStorage();
    const suffix = `:u:${_userId}`;
    const now = new Date().toISOString();
    orig.setItem(
        `${META_KEY}${suffix}`,
        JSON.stringify({ client_updated_at: now }),
    );
    return now;
}

// ─── Network ────────────────────────────────────────────────────────────

async function fetchServerState() {
    const { data } = await axios.get(STATE_URL, {
        timeout: BOOTSTRAP_TIMEOUT_MS,
        withCredentials: true,
    });
    return data;
}

async function pushSnapshot(snapshot) {
    return axios.put(
        STATE_URL,
        {
            data: snapshot.data,
            client_updated_at: snapshot.client_updated_at,
        },
        { timeout: PUSH_TIMEOUT_MS, withCredentials: true },
    );
}

function hasChatData(data) {
    const raw = data?.["toneguard:v2"];
    if (!raw || typeof raw !== "string") return false;
    try {
        const parsed = JSON.parse(raw);
        return (
            parsed &&
            Array.isArray(parsed.chats) &&
            parsed.chats.length > 0
        );
    } catch {
        return false;
    }
}

// ─── Public API ─────────────────────────────────────────────────────────

/**
 * Run the one-shot reconciliation between local and server state for the
 * given user. Returns once local storage reflects the merged truth so the
 * caller can safely mount components that read from localStorage.
 */
export async function bootstrapCloudSync(userId) {
    if (!userId) return { ok: false, reason: "no_user" };
    _userId = userId;
    _state = "bootstrapping";

    let server = null;
    try {
        server = await fetchServerState();
    } catch (e) {
        // Network or auth failure — log and continue in local-only mode.
        // We DO NOT block the UI on a sync failure. The user's local data
        // is intact; next write will retry the push.
        console.warn("cloudSync: bootstrap fetch failed", e?.message || e);
        _state = "live";
        return { ok: false, reason: "fetch_failed" };
    }

    const local = readLocalSnapshot();
    const localHasChats = hasChatData(local.data);
    const serverHasChats = hasChatData(server?.data || {});

    const serverStamp = server?.client_updated_at || null;
    const localStamp = local.client_updated_at || null;

    let resolution = "local_wins";
    if (serverHasChats && !localHasChats) {
        resolution = "server_wins";
    } else if (serverHasChats && localHasChats) {
        // Both sides have data — last-write-wins. If timestamps are equal
        // or missing on one side, prefer the side that has one.
        if (serverStamp && localStamp) {
            resolution = serverStamp >= localStamp ? "server_wins" : "local_wins";
        } else if (serverStamp && !localStamp) {
            resolution = "server_wins";
        }
    } else if (!serverHasChats && !localHasChats) {
        // Brand new user on a fresh device — nothing to merge. Treat as
        // local_wins (empty push) so we set the meta stamp.
        resolution = "local_wins";
    }

    if (resolution === "server_wins") {
        applyServerSnapshot(server.data || {}, serverStamp);
    } else {
        // Push local → server so both sides agree. Stamp meta with NOW so
        // the next device that boots can compare against it.
        const stamp = stampMetaNow();
        const updated = readLocalSnapshot();
        updated.client_updated_at = stamp;
        try {
            await pushSnapshot(updated);
        } catch (e) {
            console.warn("cloudSync: initial push failed", e?.message || e);
        }
    }

    _state = "live";
    return { ok: true, resolution };
}

/**
 * Tear down on logout / user switch. Cancels any pending push.
 */
export function teardownCloudSync() {
    _userId = null;
    _state = "idle";
    if (_pushTimer) {
        clearTimeout(_pushTimer);
        _pushTimer = null;
    }
    _pushInFlight = null;
}

/**
 * Storage-scope write hook. Called from storageScope on every setItem /
 * removeItem of a `toneguard:*` key. Debounces a full snapshot push so
 * we never thrash the network for a flurry of writes (e.g., during a
 * single chat-message persist).
 */
export function notifyWrite() {
    if (_state !== "live" || !_userId) return;
    if (_pushTimer) clearTimeout(_pushTimer);
    _pushTimer = setTimeout(async () => {
        _pushTimer = null;
        const stamp = stampMetaNow();
        const snapshot = readLocalSnapshot();
        snapshot.client_updated_at = stamp;
        try {
            // Track the latest push so retries can wait it out if needed.
            _pushInFlight = pushSnapshot(snapshot);
            await _pushInFlight;
        } catch (e) {
            // Silent — the next write will retry. Network blips and 401s
            // during a refresh should not surface as a toast.
            console.warn("cloudSync: push failed", e?.message || e);
        } finally {
            _pushInFlight = null;
        }
    }, PUSH_DEBOUNCE_MS);
}

/**
 * Force an immediate push of the current snapshot. Used by Profile →
 * Reset device data flows so a delete is reflected server-side without
 * waiting for the debounce.
 */
export async function flushCloudSync() {
    if (_state !== "live" || !_userId) return;
    if (_pushTimer) {
        clearTimeout(_pushTimer);
        _pushTimer = null;
    }
    const stamp = stampMetaNow();
    const snapshot = readLocalSnapshot();
    snapshot.client_updated_at = stamp;
    try {
        await pushSnapshot(snapshot);
    } catch (e) {
        console.warn("cloudSync: flush failed", e?.message || e);
    }
}

/**
 * Wipe the user's cloud state (Profile → Reset device data wired). Local
 * state is the caller's responsibility — typically called AFTER the
 * caller removes the relevant localStorage keys themselves.
 */
export async function deleteCloudState() {
    if (!_userId) return;
    try {
        await axios.delete(STATE_URL, {
            withCredentials: true,
            timeout: PUSH_TIMEOUT_MS,
        });
    } catch (e) {
        console.warn("cloudSync: delete failed", e?.message || e);
    }
}

// Test surface.
export const __internals = {
    readLocalSnapshot,
    applyServerSnapshot,
    hasChatData,
    META_KEY,
};
